import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Headphones, Mail, Phone, MessageCircle, FileText, Package, HelpCircle, ExternalLink, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const Support = () => {
  const supportOptions = [
    {
      icon: Headphones,
      title: 'Customer Support',
      description: 'Get help with your HomeLyph products',
      link: '/customer-support',
      color: 'bg-blue-500'
    },
    {
      icon: Mail,
      title: 'Contact Us',
      description: 'Send us a message or question',
      link: '/contact',
      color: 'bg-green-500'
    },
    {
      icon: Package,
      title: 'Track Order',
      description: 'Check your order status',
      link: '/track-order',
      color: 'bg-orange-500'
    },
    {
      icon: FileText,
      title: 'Warranty',
      description: 'View warranty information and make claims',
      link: '/warranty',
      color: 'bg-purple-500'
    }
  ];

  return (
    <div className="min-h-screen bg-[#f0f4f8]">
      <Navbar />
      
      {/* Hero */}
      <div className="pt-44 pb-16 bg-gradient-to-br from-[#003456] to-[#001d30]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="w-20 h-20 bg-[#fdc31c] rounded-full flex items-center justify-center mx-auto mb-6">
            <HelpCircle className="h-10 w-10 text-[#003456]" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Support Center</h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto">
            We're here to help you get the most out of your HomeLyph freeze dryer
          </p>
        </div>
      </div>
      
      <div className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          {/* Quick Contact */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white rounded-2xl p-6 text-center shadow-sm">
              <Phone className="h-8 w-8 text-[#003456] mx-auto mb-3" />
              <h3 className="font-semibold text-[#0a0203] mb-1">Call Us</h3>
              <p className="text-gray-500 text-sm mb-2">Mon-Fri, 9am-4pm MST</p>
              <a href="tel:1-888-223-1435" className="text-[#003456] font-semibold">1-888-223-1435</a>
            </div>
            <div className="bg-white rounded-2xl p-6 text-center shadow-sm">
              <Mail className="h-8 w-8 text-[#003456] mx-auto mb-3" />
              <h3 className="font-semibold text-[#0a0203] mb-1">Email Us</h3>
              <p className="text-gray-500 text-sm mb-2">Monday-Friday</p>
              <a href="mailto:support@homelyph.com" className="text-[#003456] font-semibold">support@homelyph.com</a>
            </div>
            <div className="bg-white rounded-2xl p-6 text-center shadow-sm">
              <ExternalLink className="h-8 w-8 text-[#003456] mx-auto mb-3" />
              <h3 className="font-semibold text-[#0a0203] mb-1">Customer Portal</h3>
              <p className="text-gray-500 text-sm mb-2">Manage your account</p>
              <a href="https://freezedryersupport.com" target="_blank" rel="noopener noreferrer" className="text-[#003456] font-semibold">Open Portal →</a>
            </div>
          </div>
          
          {/* Support Categories */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {supportOptions.map((opt, i) => (
              <Link 
                key={i} 
                to={opt.link}
                className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-all group"
              >
                <div className={`w-14 h-14 ${opt.color} rounded-xl flex items-center justify-center mb-4`}>
                  <opt.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-[#0a0203] mb-2 group-hover:text-[#003456] transition-colors">
                  {opt.title}
                </h3>
                <p className="text-gray-600 mb-4">{opt.description}</p>
                <span className="text-[#003456] font-medium flex items-center gap-2">
                  Learn more <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
          
          {/* Additional Resources */}
          <div className="bg-white rounded-2xl p-8 shadow-sm">
            <h2 className="text-2xl font-bold text-[#0a0203] mb-6">Helpful Resources</h2>
            <div className="grid md:grid-cols-4 gap-4">
              <Link to="/documents?category=user-manual" className="p-4 rounded-lg bg-[#f0f4f8] hover:bg-[#dbeef5] transition-colors text-center">
                <FileText className="h-6 w-6 text-[#003456] mx-auto mb-2" />
                <h4 className="font-semibold text-[#0a0203] text-sm">User Manuals</h4>
              </Link>
              <Link to="/documents?category=quick-start" className="p-4 rounded-lg bg-[#f0f4f8] hover:bg-[#dbeef5] transition-colors text-center">
                <FileText className="h-6 w-6 text-[#003456] mx-auto mb-2" />
                <h4 className="font-semibold text-[#0a0203] text-sm">Quick Start</h4>
              </Link>
              <Link to="/return-policy" className="p-4 rounded-lg bg-[#f0f4f8] hover:bg-[#dbeef5] transition-colors text-center">
                <FileText className="h-6 w-6 text-[#003456] mx-auto mb-2" />
                <h4 className="font-semibold text-[#0a0203] text-sm">Return Policy</h4>
              </Link>
              <Link to="/accessibility" className="p-4 rounded-lg bg-[#f0f4f8] hover:bg-[#dbeef5] transition-colors text-center">
                <FileText className="h-6 w-6 text-[#003456] mx-auto mb-2" />
                <h4 className="font-semibold text-[#0a0203] text-sm">Accessibility</h4>
              </Link>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default Support;
